<template>
  <BaseLayout>
    <BaseDashboard :is-loading="isLoading">
      <div class="h-100 overflow-auto">
        <BaseWizardHeader
          :active-index="activeIndex"
          :tabs="wizardSteps"
          :title="wizardSteps[activeIndex].title"
          :disable-override="isEditingCampaign && !isNotValidNextButton"
          @update-index="updateActiveIndex"
        >
          <feature
            name="SHOWCASE_CREATIVE"
          >
            <router-link
              v-if="activeIndex === 2"
              class="share-btn btn btn-secondary btn-sm mr-3"
              :to="{
                name: 'Showcase',
                params: {
                  creativeKey: creativeKey,
                }
              }"
              target="_blank"
              data-test="showcase-share-button"
              v-b-tooltip.hover
              title="Share a showcase of your creative and content with third parties"
              @click.native.capture="handleClick"
              :disabled="isSelfServiceCampaignAccessedByAdmin"
            >
              Share preview
              <i class="fal fa-share-square ml-2" />
            </router-link>
          </feature>
        </BaseWizardHeader>
        <b-row
          class="m-0 page-wrapper"
          v-if="activeIndex < 2"
        >
          <b-col
            class="p-5"
            cols="auto"
            lg="8"
          >
            <CampaignInfo
              ref="campaignInfo"
              v-if="activeIndex === 0"
              @updateOrderDetails="updateOrderDetails"
              @setNotValidForNavigation="updateIsNotValidForNavigation"
              @setIsDirty="setIsDirty"
              :key="campaignInfoKey"
            />
            <ContentInfo
              ref="contentInfo"
              v-if="activeIndex === 1"
              @setNotValidForNavigation="updateIsNotValidForNavigation"
              @update-step="updateStep"
            />
          </b-col>
          <b-col
            cols="4"
            class="p-5"
          >
            <CampaignDetailsCard
              ref="CampaignDetailsCard"
            />
          </b-col>
        </b-row>

        <ContentEditor
          v-if="activeIndex === 2"
          :show-footer="true"
          view-type="Setup"
          @setNotValidForNavigation="updateIsNotValidForNavigation"
        />
        <BaseWizardFooter
          id="fixed-footer"
          v-if="activeIndex < 4"
          @next-step="onNextStep"
          @previous-step="onPreviousStep"
          :next-disabled="isNotValidNextButton"
          :last-disabled="isNotValidLastButton"
          :show-back-button="showBackButton"
          :is-loading="isNextButtonLoading"
        />
        <CampaignSummary
          v-if="activeIndex === 3"
          @next-step="onNextStep"
          @edit-campaign="onEditCampaign"
        />
        <CampaignInvoicing
          v-if="activeIndex === 4"
          :active-index="activeIndex"
        />
      </div>
    </BaseDashboard>
  </BaseLayout>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as accountTypes from '@/modules/account/store/types';
import CampaignInfo from '@/modules/campaigns/components/CampaignInfo';
import CampaignSummary from '@/modules/campaigns/components/CampaignSummary';
import CampaignInvoicing from '@/modules/campaigns/components/CampaignInvoicing';
import CampaignDetailsCard from '@/modules/campaigns/components/CampaignDetailsCard';
import ContentInfo from '@/modules/content/components/ContentInfo';
import ContentEditor from '@/modules/content/components/ContentEditor';

import wizardStepItems from './NewCampaignStepItems';

export default {
  name: 'NewCampaign',
  components: {
    CampaignInfo,
    CampaignDetailsCard,
    ContentInfo,
    ContentEditor,
    CampaignSummary,
    CampaignInvoicing,
  },
  data() {
    return {
      isLoading: true,
      campaignId: null,
      newCampaign: {
        title: '',
      },
      clicked: false,
      activeIndex: 0,
      ...wizardStepItems,
      campaignInfoKey: 0,
      isNotValidLastButton: true,
      isNotValidNextButton: true,
      isNextButtonLoading: false,
      isDirty: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapGetters('campaigns', {
      isSelfServiceCampaign: campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN,
      isSelfServiceCampaignAccessedByAdmin: campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
    }),
    ...mapState('account', {
      company: 'companyName',
    }),
    ...mapState('content', [
      'activeCreatives',
      'creative',
    ]),
    showBackButton() {
      return this.activeIndex > 0;
    },
    isEditingCampaign() {
      return !!(this.campaignId);
    },
    creativeKey() {
      return this.creative.paused ? this.activeCreatives[0].creativeKey : this.creative.creativeKey;
    },
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_INVOICING_DETAILS,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.FETCH_EXISTING_CONTENT_PROVIDER,
      campaignTypes.FETCH_EXISTING_ADVERTISERS,
      campaignTypes.CREATE_ADVERTISER,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_BANNED_WORDS,
      campaignTypes.FETCH_EXISTING_CAMPAIGN,
      campaignTypes.FETCH_ORDER,
      campaignTypes.FETCH_EXISTING_BLACKLIST,
      campaignTypes.RESET_CAMPAIGN,
      campaignTypes.UPDATE_SELECTED_ADVERTISER,
      campaignTypes.SET_VIEW_TYPE,
    ]),
    ...mapActions('content', [
      contentTypes.NEW_FETCH_CONTENT_SCRAPES,
      contentTypes.FETCH_EXISTING_CONTENT_SCRAPES,
      contentTypes.FETCH_FEED_CONTENT_ITEMS,
      contentTypes.FETCH_CONTENT_ITEMS,
      contentTypes.FETCH_EXISTING_CREATIVES,
    ]),
    handleClick(event) {
      if (this.isSelfServiceCampaignAccessedByAdmin) {
        event.preventDefault();
      }
    },
    onEstimatedNextStep() {
      const hasCampaignInfo = !!this.$refs.campaignInfo;
      if (hasCampaignInfo) {
        this.$refs.campaignInfo.onSubmit(this.isDirty)
          .then(() => {
            this.activeIndex++;
            this.isDirty = false;
            this.isNextButtonLoading = false;
          }).catch(() => {
            this.isNextButtonLoading = false;
          });
      } else {
        this.activeIndex++;
        this.isDirty = false;
        this.isNextButtonLoading = false;
      }
    },
    onContentNextStep() {
      const hasContentInfo = !!this.$refs.contentInfo;
      if (hasContentInfo) {
        this.isNextButtonLoading = true;
        this.$refs.contentInfo.onSubmit();
      }
    },
    onPreviousStep() {
      this.activeIndex--;
    },
    onNextStep() {
      if (this.activeIndex === 0) return this.onCampaignInfoNextStep();
      if (this.activeIndex === 1) return this.onContentNextStep();
      this.activeIndex++;
    },
    updateStep() {
      this.isNextButtonLoading = false;
      this.activeIndex++;
    },
    onEditCampaign() {
      this.activeIndex = 0;
    },
    async fetchCampaignPageEntities(advertiserId, campaignId) {
      try {
        await this.FETCH_EXISTING_CONTENT_PROVIDER();
        await this.FETCH_CURRENCIES();
        await this.FETCH_BANNED_WORDS();
        if (typeof campaignId !== 'undefined') {
          this.fetchExistingCampaignEntities(campaignId);
        } else {
          this.isLoading = false;
        }
      } catch (err) {
        this.$router.replace('/dashboard');
      }
    },
    async fetchExistingCampaignEntities(campaignId) {
      try {
        const { advertiserId } = this.$route.params;
        const campaign = await this.FETCH_EXISTING_CAMPAIGN({ advertiserId, campaignId });
        if (typeof campaign.state !== 'undefined' && campaign.state !== 'DRAFT') {
          this.$router.replace('/dashboard');
        }
        this.FETCH_EXISTING_BLACKLIST(campaign.blacklistRuleSetId);
        await this.FETCH_ORDER({ advertiserId, campaignId, setOrder: true });
        const creativesResponse = await this.FETCH_EXISTING_CREATIVES({ advertiserId });
        await this.NEW_FETCH_CONTENT_SCRAPES();
        await this.FETCH_EXISTING_CONTENT_SCRAPES({ advertiserId, feedId: creativesResponse.feedId });
        await this.FETCH_FEED_CONTENT_ITEMS(creativesResponse.feedId);
        this.isLoading = false;
      } catch (err) {
        this.$router.replace('/dashboard');
      }
    },
    updateOrderDetails(payload) {
      if ((this.$refs.CampaignDetailsCard)) {
        this.$refs.CampaignDetailsCard.setOrderDetails(payload);
      }
    },
    async handleCreate() {
      this.isLoading = true;
      const { campaignId, advertiserId } = this.$route.params;
      this.campaignId = campaignId;
      this.RESET_CAMPAIGN();
      try {
        const advertisers = await this.FETCH_EXISTING_ADVERTISERS();
        const aid = Number.parseInt(advertiserId, 10) || advertisers[0].id;
        const advertiserSet = advertisers
          .find((advertiser) => advertiser.id === aid);
        if (advertiserId && advertiserSet) {
          await this.UPDATE_SELECTED_ADVERTISER(advertiserSet);
        }
        this.fetchCampaignPageEntities(aid, campaignId);
      } catch (err) {
        this.$router.replace('/dashboard');
      }
    },
    onCampaignInfoNextStep() {
      this.isNextButtonLoading = true;
      this.onEstimatedNextStep();
    },
    updateIsNotValidForNavigation(type, valid) {
      if (type === 'last') {
        this.isNotValidLastButton = valid;
      } else if (type === 'next') {
        this.isNotValidNextButton = valid;
      }
    },
    updateActiveIndex(index) {
      if (this.isDirty) {
        this.warnUserOfUnsavedChanges()
          .then(() => {
            this.activeIndex = index;
            this.isDirty = false;
          }).catch(() => {});
      } else {
        this.activeIndex = index;
      }
    },
    warnUserOfUnsavedChanges() {
      return new Promise((resolve, reject) => {
        this.$bvModal.msgBoxConfirm('You have unsaved changes! \n"Yes" to proceed without saving, \n"No" to cancel this operation.', {
          title: 'Warning',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
          .then((value) => {
            if (!value) return reject();
            return resolve();
          })
          .catch(() => reject());
      });
    },
    setIsDirty() {
      this.isDirty = true;
    },
  },
  created() {
    this.handleCreate();
    this.SET_VIEW_TYPE('Setup');
  },
  watch: {
    $route() {
      this.onEditCampaign();
      this.handleCreate();
      this.$forceUpdate();
    },
  },
};
</script>
<style scoped>
.page-wrapper{
  padding-bottom: 69px;
  height: calc(100% - 65px);
}
#fixed-footer{
  position: fixed;
  bottom: 0;
  left: 16.66667%;
  right: auto;
  width: calc(100% - 16.66667%);
}
.share-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.65;
}
</style>
